import Tesseract from 'tesseract.js';
import { NRICData } from '../types/nric';

export class OCRProcessor {
  private worker: Tesseract.Worker | null = null;

  async initialize(): Promise<void> {
    if (this.worker) {
      await this.worker.terminate();
    }
    
    this.worker = await Tesseract.createWorker('eng');
    await this.worker.setParameters({
      tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789 .-/()',
      tessedit_pageseg_mode: Tesseract.PSM.SINGLE_BLOCK,
    });
  }

  async processImage(
    imageFile: File, 
    onProgress?: (progress: number) => void
  ): Promise<{ text: string; confidence: number }> {
    if (!this.worker) {
      await this.initialize();
    }

    // Create a progress simulation since we can't use the logger function
    // The logger function causes DataCloneError when passed to Web Worker
    let progressInterval: NodeJS.Timeout | null = null;
    
    if (onProgress) {
      let currentProgress = 0;
      progressInterval = setInterval(() => {
        if (currentProgress < 90) {
          currentProgress += Math.random() * 15; // Random increment
          onProgress(Math.min(currentProgress, 90));
        }
      }, 200);
    }

    try {
      const result = await this.worker!.recognize(imageFile);
      
      // Complete the progress
      if (onProgress) {
        onProgress(100);
      }

      return {
        text: result.data.text,
        confidence: result.data.confidence
      };
    } finally {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
    }
  }

  parseNRICData(rawText: string): NRICData | null {
    try {
      const lines = rawText.split('\n').map(line => line.trim()).filter(line => line.length > 0);
      
      let name = '';
      let phone = '';
      let nricNumber = '';

      // Myanmar NRIC patterns - multiple variations for OCR flexibility
      const nricPattern = /(\d{1,2})\s*\/\s*([A-Za-z]{3,8})\s*\(\s*(?:N|NAING)\s*\)\s*(\d{6})/i;
      const nricFlexiblePattern = /(\d{1,2})\s*[\/\\]\s*([A-Za-z]{3,8})\s*[\(\[]?\s*(?:N|NAING)\s*[\)\]]?\s*(\d{6})/i;
      const nricVeryFlexiblePattern = /(\d{1,2})\s*[\/\\|]\s*([A-Za-z]{2,10})\s*[\(\[]?\s*(?:N|NAING|n|naing)\s*[\)\]]?\s*(\d{5,7})/i;

      // Myanmar phone patterns
      const phonePattern = /^(?:\+?95|0)?[1-9]\d{7,9}$/;
      const phoneFlexiblePattern = /(?:\+?95\s?|0)?\s?[1-9]\s?\d{1,3}\s?\d{2,3}\s?\d{3,4}/;

      // Name pattern - typically appears after "Name" or similar keywords
      const namePattern = /(?:name|nama)\s*:?\s*([A-Za-z\s]+)/i;

      for (const line of lines) {
        // Extract NRIC number - try multiple patterns
        let nricMatch = line.match(nricPattern);
        if (!nricMatch) {
          nricMatch = line.match(nricFlexiblePattern);
        }
        if (!nricMatch) {
          nricMatch = line.match(nricVeryFlexiblePattern);
        }
        
        if (nricMatch && !nricNumber) {
          // Reconstruct proper NRIC format: 12/MaGaDa(N)123456
          const [, region, township, serial] = nricMatch;
          // Ensure serial number is 6 digits (pad if needed)
          const paddedSerial = serial.padStart(6, '0');
          nricNumber = `${region}/${township.toUpperCase()}(N)${paddedSerial}`;
          continue;
        }
        console.log("line", line)
        console.log("nric", nricNumber)

        // Extract phone number
        let phoneMatch = line.match(phonePattern);
        if (!phoneMatch) {
          // Try flexible pattern for OCR text with potential formatting issues
          phoneMatch = line.match(phoneFlexiblePattern);
        }
        
        if (phoneMatch && !phone) {
          // Clean up the phone number (remove spaces, country code formatting)
          phone = phoneMatch[0].replace(/\s+/g, '').replace(/^\+?95/, '0');
          continue;
        }
        console.log(phone)

        // Extract name
        const nameMatch = line.match(namePattern);
        if (nameMatch && !name) {
          name = nameMatch[1].trim();
          continue;
        }
        console.log("name", name)

      }

      return {
        name: name || 'Not found',
        phone: phone || 'Not found',
        nricNumber: nricNumber || 'Not found'
      };
    } catch (error) {
      console.error('Error parsing NRIC data:', error);
      return null;
    }
  }

  async terminate(): Promise<void> {
    if (this.worker) {
      await this.worker.terminate();
      this.worker = null;
    }
  }
}

export const ocrProcessor = new OCRProcessor();
